import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getTeam, createTeamMember, updateTeamMember, deleteTeamMember } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, X, Linkedin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export default function AdminTeam() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    image: '',
    linkedin: '',
    order: 0,
  });

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const res = await getTeam();
      setTeam(res.data);
    } catch (err) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingMember) {
        await updateTeamMember(editingMember.id, formData);
        toast.success('Team member updated');
      } else {
        await createTeamMember(formData);
        toast.success('Team member added');
      }
      loadTeam();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      image: member.image,
      linkedin: member.linkedin || '',
      order: member.order || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      await deleteTeamMember(id);
      toast.success('Team member deleted');
      loadTeam();
    } catch (err) {
      toast.error('Failed to delete team member');
    }
  };

  const resetForm = () => {
    setEditingMember(null);
    setFormData({ name: '', role: '', image: '', linkedin: '', order: 0 });
  };

  return (
    <AdminLayout title="Team Management">
      <div className="space-y-6" data-testid="admin-team">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Team Members</h1>
            <p className="text-[#888] text-sm">Manage your team displayed on the website</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button
                data-testid="add-team-btn"
                className="bg-[#ff5a1f] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-[#e04e1a] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Member
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">
                  {editingMember ? 'Edit Team Member' : 'Add Team Member'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="team-form">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Role *</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="Growth Engineer"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Image URL *</label>
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">LinkedIn URL</label>
                  <input
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingMember ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Team Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : team.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((member) => (
              <div
                key={member.id}
                className="bg-[#0A0A0A] border border-[#1F1F1F] overflow-hidden group"
                data-testid={`team-card-${member.id}`}
              >
                <div className="aspect-square overflow-hidden bg-[#121212]">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-syne font-bold text-white">{member.name}</h3>
                  <p className="text-[#888] text-sm mb-3">{member.role}</p>
                  
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ff5a1f] text-sm flex items-center gap-1 mb-4 hover:underline"
                    >
                      <Linkedin className="w-4 h-4" />
                      LinkedIn
                    </a>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(member)}
                      className="flex-1 border border-[#333] text-white px-3 py-2 text-sm flex items-center justify-center gap-2 hover:border-[#ff5a1f] hover:text-[#ff5a1f]"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="border border-[#333] text-red-500 px-3 py-2 hover:bg-red-500/10 hover:border-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888] mb-4">No team members yet</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="text-[#ff5a1f] font-semibold hover:underline"
            >
              Add your first team member
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
